import z from 'zod';
import { Percent } from '../percent.js';

// Custom treatment of custom vat suffix percentage, 26XX accounts
const vatPercentageSuffixes = ['A', 'B', 'C', 'D', 'E', 'None'] as const;
export type VatPercentageSuffix = (typeof vatPercentageSuffixes)[number];

// Reported (incoming vat) fields: rate is NOT encoded in the field number — must be tracked explicitly via A/B/C.
// Fields 05–08 (TaxableSalesBasis), 20–24 (ReverseChargeVatBasis), 50 (ImportsBasis).
export const REPORTED_VAT_SUFFIXES = ['A', 'B', 'C', 'D'] as const;
export const reportedVatSuffixSchema = z.enum(REPORTED_VAT_SUFFIXES).or(z.literal('None'));
export type ReportedVatSuffix = z.infer<typeof reportedVatSuffixSchema>;
export function defaultReportedVatSuffix(vat: Percent): ReportedVatSuffix {
  const f = Percent.fraction(vat);
  if (Math.abs(f - 0.25) < 0.001) return 'A';
  if (Math.abs(f - 0.12) < 0.001) return 'B';
  if (Math.abs(f - 0.06) < 0.001) return 'C';
  if (f < 0.001) return 'D';
  return 'A';
}

export const SHADOW_VAT_SUFFIXES = ['A', 'E', 'B', 'C', 'D'] as const; // Order is because E=12.5 is half of A=25
export const shadowVatSuffixSchema = z.enum(SHADOW_VAT_SUFFIXES).or(z.literal('None'));
export type ShadowVatSuffix = z.infer<typeof shadowVatSuffixSchema>;
export function defaultShadowVatSuffix(vat: Percent): ShadowVatSuffix {
  const f = Percent.fraction(vat);
  if (Math.abs(f - 0.625) < 0.001) return 'E';
  if (Math.abs(f - 0.25) < 0.001) return 'A';
  if (Math.abs(f - 0.12) < 0.001) return 'B';
  if (Math.abs(f - 0.06) < 0.001) return 'C';
  if (f < 0.001) return 'D';
  return 'A';
}

// Percentages (0–100 scale) for each field variant.
// Field 48 = sum over variants of: Field_480_X_amount * FIELD_480_PERCENTAGES[X] / 100
export const VAT_SUFFIX_PERCENTAGES: Record<VatPercentageSuffix, number> = {
  A: 25,
  B: 12,
  C: 6,
  D: 0,
  E: 12.5 / 112.5,
  None: 0,
};

// Maps each variant to the Percent fraction used for percent on FlatRow,
// consistent with the thresholds in defaultShadowVatSuffix().
export const VAT_SUFFIX_TO_PERCENT: Record<VatPercentageSuffix, Percent> = {
  A: Percent.fromNumber(25),
  B: Percent.fromNumber(12),
  C: Percent.fromNumber(6),
  D: Percent.fromNumber(0),
  E: Percent.fromNumber(62.5),
  None: Percent.fromNumber(0),
};

export const VAT_SUFFIX_DESCRIPTION: Record<VatPercentageSuffix, string> = {
  A: '25%',
  B: '12%',
  C: '6%',
  D: 'Ingen moms',
  E: 'Halv moms',
  None: '-',
};
